import { Table, TableColumnsType, Tag, Tooltip } from 'antd';
import {
	ApplicationSummary,
	ApplicationTableDataType,
	Batch,
	Pagination,
	PagingConfig,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useApplicationStatusTranslation } from '../../../hooks/useApplicationStatusTranslation';
import { useTranslation } from 'react-i18next';
import {
	getFullName,
	hasPrivilege,
	isProposerSkippedToEnd,
	tablePaginationConfiguration,
} from '../../../shared/Utils';
import InCompleteCheck from '../../../assets/images/Incomplete-check.svg';
import CompleteCheck from '../../../assets/images/complete-check.svg';
import NotApprovedCross from '../../../assets/images/not-approved.svg';
import ProposerReassignIcon from '../../../assets/images/proposer-reassign.svg';
import EvaluteBlueIcon from '../../../assets/images/evaluate-blue.svg';
import ZoneChangeBlueIcon from '../../../assets/images/zone-change-blue.svg';
import ProposerReassignBlueIcon from '../../../assets/images/proposer-reassign-blue.svg';
import { ApplicationStatus, ReviewAction } from '../../../shared/Constants';
import { useCallback, useEffect, useState } from 'react';
import { useBatchId } from '../../../hooks/useBatchId';
import { PROPOSER_REASSIGN } from '../../../shared/Routes';
import { ZoneChangePopup } from './Zone-Change/ZoneChangePopup';
import useUserSession from '../../../hooks/useUserSession';
import { PrivilegeCodes } from '../../../shared/RoleConstants';

interface DashboardTableProps {
	applications: ApplicationSummary[] | undefined;
	onEvaluateClick: (application: ApplicationSummary) => void;
	onVerifyResultsClick: (application: ApplicationSummary) => void;
	onPageChange: (page: number, pageSize: number) => void;
	pageConfig: PagingConfig;
	pagination: Pagination;
}

export const DashboardTable = (props: DashboardTableProps) => {
	const {
		applications,
		onEvaluateClick,
		onPageChange,
		pageConfig,
		pagination,
	} = props;

	const { getTranslatedValue } = useApplicationStatusTranslation();
	const { selectedRole, loggedInUserPrivileges } = useUserSession();
	const { t } = useTranslation();
	const { navigateToRoute } = useBatchId();

	const [isZoneChangeModalOpen, setIsZoneChangeModalOpen] = useState(false);
	const [zoneChangeApplication, setZoneChangeApplication] =
		useState<ApplicationTableDataType>();
	const [disableTableActionsForPastBatch, setDisableTableActionsForPastBatch] =
		useState<boolean>(false);

	const isZC = selectedRole === UserRole.ZC;
	const isCareTeam = selectedRole === UserRole.CARE_TEAM;
	const isScreeningAdmin = selectedRole === UserRole.SCREENING_ADMIN;

	const tablePaginationConfig = tablePaginationConfiguration(
		pageConfig,
		pagination,
		onPageChange
	);

	const handleEvaluateClick = (application: ApplicationTableDataType) => {
		onEvaluateClick(application.originalApplication);
	};

	const handleProposerReassign = useCallback(
		(application: ApplicationTableDataType) => {
			if (application.fullName) {
				const applicationRecord = application.originalApplication;
				sessionStorage.setItem(
					'SELECTED_APPLICATION',
					JSON.stringify(applicationRecord)
				);
				navigateToRoute(PROPOSER_REASSIGN);
				sessionStorage.setItem('screeningAdmin', 'Screening Admin');
			}
		},
		[navigateToRoute]
	);

	const handleZoneChangeModalOpen = () => {
		setIsZoneChangeModalOpen(true);
	};

	const handleZoneChangeModalClose = () => {
		setIsZoneChangeModalOpen(false);
	};

	const handleZoneChangeClick = (application: ApplicationTableDataType) => {
		setZoneChangeApplication(application);
		handleZoneChangeModalOpen();
	};

	const applicationTableData = applications?.map((application) => {
		let statusValue = '';
		let proposerNameValue = '';
		let proposerSRCMIDValue = '';
		const applicationStatus = application?.applicationStatus;
		statusValue = getTranslatedValue(application.applicationStatus) || '';
		const isWaitingForProposer =
			applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED;
		const isProposerYetToBeAssigned =
			applicationStatus === ApplicationStatus.USER_SUBMITTED_APPLICATION;
		const isWaitingForCareTeam =
			applicationStatus === ApplicationStatus.WAITING_FOR_CARE_TEAM;
		const isProposerFinalRecommendation =
			applicationStatus === ApplicationStatus.FUNCTIONARY_SUBMITTED;
		proposerNameValue = application.proposerName || '';
		proposerSRCMIDValue = application.proposerSrcmId || '';

		const statusStyle =
			applicationStatus === ApplicationStatus.APPROVED
				? 'bg-green-10 border border-solid border-green text-green'
				: applicationStatus === ApplicationStatus.REJECTED
				? 'bg-red-10 border border-solid border-red-200 text-red-200'
				: 'bg-orange-10 border border-solid border-orange text-orange';

		const tagClassName = `${statusStyle} text-md rounded-full p-2`;

		const tableData: ApplicationTableDataType = {
			key: application.applicant.srcmid,
			fullName: getFullName(application.applicant),
			idCard: application.applicant.srcmid,
			gender: application.applicant.gender ?? '',
			center: application.center.name ?? '',
			zone: application.center.zone,
			proposerName: proposerNameValue,
			proposerSRCMID: proposerSRCMIDValue,
			status: <Tag className={tagClassName}>{statusValue}</Tag>,

			...(isProposerFinalRecommendation ||
			isProposerYetToBeAssigned ||
			isWaitingForCareTeam
				? {}
				: {
						application: application.applicantAnswersReviewed ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						preceptor: application.preceptorInterviewDone ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						candidate: application.applicantInterviewDone ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						family: application.familyMemberInterviewDone ? (
							<img src={CompleteCheck} alt="Complete" className="w-5 h-5" />
						) : (
							<img src={InCompleteCheck} alt="Incomplete" className="w-5 h-5" />
						),
						otherReference: application.otherReferenceProvided ? (
							<img
								src={
									application.otherReferenceInterviewDone
										? CompleteCheck
										: InCompleteCheck
								}
								alt="Check"
								className="w-5 h-5"
							/>
						) : (
							<>NA</>
						),
				  }),

			...(isWaitingForProposer ||
			isProposerYetToBeAssigned ||
			isWaitingForCareTeam
				? {}
				: {
						preceptorRecommendation: application.preceptorRecommendation
							?.review ? (
							<img
								src={
									application.preceptorRecommendation?.review ===
									ReviewAction.APPROVE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
						proposerRecommendation: application.proposerRecommendation
							?.review ? (
							<img
								src={
									application.proposerRecommendation?.review ===
									ReviewAction.APPROVE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
						ZCRecommendation: application.zcRecommendation?.review ? (
							<img
								src={
									application.zcRecommendation?.review ===
										ReviewAction.APPROVE ||
									application.zcRecommendation?.review ===
										ReviewAction.APPROVE_IDK_CANDIDATE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
						careTeamRecommendation: !application.shouldCareTeamReview ? (
							<>NA</>
						) : application.careTeamRecommendation?.review ? (
							<img
								src={
									application.careTeamRecommendation?.review ===
									ReviewAction.APPROVE
										? CompleteCheck
										: isProposerSkippedToEnd(
												application?.proposerRecommendation
										  )
										? InCompleteCheck
										: NotApprovedCross
								}
								alt="Incomplete Images"
								className="w-5 h-5"
							/>
						) : (
							<>
								<img
									src={InCompleteCheck}
									alt="Incomplete Images"
									className="w-5 h-5"
								/>
							</>
						),
				  }),
			originalApplication: application,
			applicationStatus,
		};

		return tableData;
	});

	const isWaitingForProposer = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.FUNCTIONARY_ASSIGNED
	);
	const isProposerFinalRecommendation = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.FUNCTIONARY_SUBMITTED
	);
	const isApproved = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.APPROVED
	);
	const isNotGoingForward = applicationTableData?.every(
		(row) => row.applicationStatus === ApplicationStatus.REJECTED
	);

	const showAllColumns = isApproved || isNotGoingForward;

	const hideInterviewColumns = isZC || isCareTeam;

	const showView = hasPrivilege(
		PrivilegeCodes.GET_CANDIDATE_SCREENING_PROGRESS_SUMMARY,
		loggedInUserPrivileges
	);

	const showProposerReAssign = hasPrivilege(
		PrivilegeCodes.REASSIGN_FUNCTIONARY_TO_APPLICATION,
		loggedInUserPrivileges
	);

	const showZoneChange = hasPrivilege(
		PrivilegeCodes.UPDATE_REGION_ZONE_CENTER_OF_APPLICATION,
		loggedInUserPrivileges
	);

	const baseColumns: TableColumnsType<ApplicationTableDataType> = [
		{
			title: t('fullName'),
			dataIndex: 'fullName',
			key: 'fullName',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
		},
		{
			title: t('idCard'),
			dataIndex: 'idCard',
			key: 'idCard',
		},
		{
			title: t('gender'),
			dataIndex: 'gender',
			key: 'gender',
			render: (__, record, index) => (
				<span key={index}>
					<span className="ml-2">
						{record?.gender
							? record.gender.charAt(0).toUpperCase() +
							  record.gender.slice(1).toLowerCase()
							: ''}
					</span>
				</span>
			),
		},
		{
			title: t('center'),
			dataIndex: 'center',
			key: 'center',
			className: 'whitespace-nowrap',
		},
		{
			title: t('zone'),
			dataIndex: 'zone',
			key: 'zone',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
		},
		{
			title: t('proposerName'),
			dataIndex: 'proposerName',
			key: 'proposerName',
			width: 150,
			className: 'whitespace-nowrap',
		},
		{
			title: t('proposerSRCMID'),
			dataIndex: 'proposerSRCMID',
			key: 'proposerSRCMID',
		},
	];

	if (!hideInterviewColumns) {
		if (isWaitingForProposer || showAllColumns) {
			baseColumns.push(
				{
					title: t('application'),
					dataIndex: 'application',
					key: 'application',
					align: 'center',
				},
				{
					title: t('candidate'),
					dataIndex: 'candidate',
					key: 'candidate',
					align: 'center',
				},
				{
					title: t('preceptor'),
					dataIndex: 'preceptor',
					key: 'preceptor',
					align: 'center',
				},
				{
					title: t('family'),
					dataIndex: 'family',
					key: 'family',
					align: 'center',
				},
				{
					title: t('otherReference'),
					dataIndex: 'otherReference',
					key: 'otherReference',
					align: 'center',
				}
			);
		}

		if (isProposerFinalRecommendation || showAllColumns) {
			baseColumns.push(
				{
					title: t('preceptorRecommendation'),
					dataIndex: 'preceptorRecommendation',
					key: 'preceptorRecommendation',
					align: 'center',
				},
				{
					title: t('proposerRecommendation'),
					dataIndex: 'proposerRecommendation',
					key: 'proposerRecommendation',
					align: 'center',
				},
				{
					title: t('ZCRecommendation'),
					dataIndex: 'ZCRecommendation',
					key: 'ZCRecommendation',
					align: 'center',
				},
				{
					title: t('careTeamRecommendation'),
					dataIndex: 'careTeamRecommendation',
					key: 'careTeamRecommendation',
					align: 'center',
				}
			);
		}
	}

	if (disableTableActionsForPastBatch) {
		baseColumns.push({
			title: t('action'),
			key: 'operation',
			fixed: 'right',
			width: isScreeningAdmin ? 100 : 150,
			render: (__, record) => {
				const status = record.applicationStatus;
				const isProposerReassignVisible =
					showProposerReAssign &&
					status &&
					[
						ApplicationStatus.USER_SUBMITTED_APPLICATION,
						ApplicationStatus.FUNCTIONARY_ASSIGNED,
						ApplicationStatus.FUNCTIONARY_SUBMITTED,
					].includes(status);

				const isProposerReassignDisabled =
					status &&
					[
						ApplicationStatus.WAITING_FOR_CARE_TEAM,
						ApplicationStatus.APPROVED,
						ApplicationStatus.REJECTED,
					].includes(status);
				return (
					<div className="flex flex-row gap-4">
						{isProposerReassignVisible ? (
							<div
								className={`cursor-pointer ${
									isProposerReassignDisabled
										? 'opacity-50 cursor-not-allowed pointer-events-none'
										: ''
								}`}
								onClick={() => {
									if (
										record.applicationStatus ===
											ApplicationStatus.FUNCTIONARY_ASSIGNED ||
										record.applicationStatus ===
											ApplicationStatus.USER_SUBMITTED_APPLICATION
									) {
										handleProposerReassign(record);
									}
								}}
							>
								<Tooltip title={t('proposerReassign')}>
									<img
										src={
											record.applicationStatus ===
												ApplicationStatus.USER_SUBMITTED_APPLICATION ||
											record.applicationStatus ===
												ApplicationStatus.FUNCTIONARY_ASSIGNED
												? ProposerReassignBlueIcon
												: ProposerReassignIcon
										}
										className="w-10 h-10"
									/>
								</Tooltip>
							</div>
						) : (
							<div className="w-5 h-5"></div>
						)}

						{showZoneChange && (
							<div
								className="cursor-pointer"
								onClick={() => handleZoneChangeClick(record)}
							>
								<Tooltip title={t('zoneChange')}>
									<img src={ZoneChangeBlueIcon} className="w-10 h-10" />
								</Tooltip>
							</div>
						)}

						{showView && (
							<div
								className="cursor-pointer"
								onClick={() => handleEvaluateClick(record)}
							>
								<Tooltip title={t('view')}>
									<img src={EvaluteBlueIcon} className="w-10 h-10" />
								</Tooltip>
							</div>
						)}
					</div>
				);
			},
		});
	}

	const columns = [...baseColumns];

	columns.splice(2, 0, {
		title: t('status'),
		dataIndex: 'status',
		key: 'status',
	});

	useEffect(() => {
		const currentActiveBatch = localStorage.getItem('CURRENT_ACTIVE_BATCH');
		if (currentActiveBatch) {
			const parsedData: Batch = JSON.parse(currentActiveBatch) as Batch;
			if (parsedData.status === 'ACTIVE') {
				setDisableTableActionsForPastBatch(true);
			} else {
				setDisableTableActionsForPastBatch(false);
			}
		}
	}, []);

	return (
		<>
			<Table
				columns={columns}
				dataSource={applicationTableData}
				pagination={tablePaginationConfig}
				scroll={{ x: 'max-content' }}
				rowClassName={() => 'border-b border-gray-300 bg-white'}
				className="w-full overflow-x-auto !border-separate !border-spacing-y-0"
			/>
			<ZoneChangePopup
				open={isZoneChangeModalOpen}
				onClose={handleZoneChangeModalClose}
				application={zoneChangeApplication}
			/>
		</>
	);
};
